import { colors } from "@indeed/ifl-tokens/react-native";
import type { ComponentType } from "react";
import type { ColorValue } from "react-native";

import { RecentSearch, Responsive, Show } from "../../../../../../mads/icons";
import type { FixedIconProps } from "../../../../../../mads/icons/Icon";
import { useI18n } from "../../../../../../shared/I18n";

export interface EmployerInsightsData {
  icon: ComponentType<FixedIconProps>;
  iconColor: ColorValue;
  headingText: string;
  descriptions?: string[];
}

/**
 * Custom hook that builds employer insights display data from the employer insights model.
 *
 * Classifies employer responsiveness into 4 categories (in priority order):
 * 1. Highly responsive: >50% response rate AND <14 days response time
 * 2. Responsive: >50% response rate AND ≥14 days response time
 * 3. Actively reviewing: Fallback when employer is actively reviewing applications
 * 4. Last active: Fallback showing when employer was last active
 *
 * @param employerInsightsModel - Data about employer responsiveness and activity
 * @returns Structured data for displaying employer insights, or undefined if no data available
 */
export function useBuildEmployerInsightsData(
  employerInsightsModel: component.employerinsights.EmployerInsightsModel | undefined,
): EmployerInsightsData | undefined {
  const gt = useI18n();

  if (!employerInsightsModel?.hasAccess) {
    return undefined;
  }

  const isHighResponseRate = employerInsightsModel.percentageResponsiveness > 50;
  const isFastResponse = employerInsightsModel.responseTimeInDays < 14;

  // Highly responsive employer: >50% response rate & <14d response time
  if (isHighResponseRate && isFastResponse && employerInsightsModel.responseTimeInDays > 0) {
    const descriptions: string[] = [
      gt.npgettext(
        "Employer responsiveness time",
        "Typically responds within {0} day",
        "Typically responds within {0} days",
        employerInsightsModel.responseTimeInDays,
        employerInsightsModel.responseTimeInDays.toString(),
      ),
    ];

    if (employerInsightsModel.lastActiveTimeInDays > -1) {
      descriptions.push(
        gt.npgettext(
          "Employer Insights Note about employer last active time",
          "Active {0} day ago",
          "Active {0} days ago",
          employerInsightsModel.lastActiveTimeInDays,
          employerInsightsModel.lastActiveTimeInDays.toString(),
        ),
      );
    }

    return {
      icon: Responsive,
      iconColor: colors.expressiveAqua700,
      headingText: gt.pgettext(
        "Employer Insights highly responsive employer heading",
        "Highly responsive employer",
      ),
      descriptions: descriptions,
    };
  }

  // Responsive employer: >50% response rate & 14d+ response time
  if (isHighResponseRate && employerInsightsModel.responseTimeInDays >= 14) {
    const descriptions: string[] = [
      gt.pgettext(
        "Employer Insights responded to percentage of applications",
        "Responded to {0}% of applications",
        employerInsightsModel.percentageResponsiveness.toString(),
      ),
    ];

    if (employerInsightsModel.lastActiveTimeInDays > -1) {
      descriptions.push(
        gt.npgettext(
          "Employer Insights Note about employer last active time",
          "Active {0} day ago",
          "Active {0} days ago",
          employerInsightsModel.lastActiveTimeInDays,
          employerInsightsModel.lastActiveTimeInDays.toString(),
        ),
      );
    }

    return {
      icon: Responsive,
      iconColor: colors.expressiveAqua700,
      headingText: gt.pgettext(
        "Employer Insights responsive employer heading",
        "Responsive employer",
      ),
      descriptions: descriptions,
    };
  }

  // Fallback: Check if actively reviewing applications
  if (employerInsightsModel.isActivelyReviewApplications) {
    return {
      icon: Show,
      iconColor: colors.neutral1000,
      headingText: gt.pgettext(
        "Employer Insights Note about employer is actively reviewing applications",
        "Actively reviewing applications",
      ),
    };
  }

  // Fallback: Show last active time
  if (employerInsightsModel.lastActiveTimeInDays > -1) {
    return {
      icon: RecentSearch,
      iconColor: colors.neutral1000,
      headingText: gt.npgettext(
        "Employer Insights Note about employer last active time",
        "Active {0} day ago",
        "Active {0} days ago",
        employerInsightsModel.lastActiveTimeInDays,
        employerInsightsModel.lastActiveTimeInDays.toString(),
      ),
    };
  }

  return undefined;
}
